import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/feature/Layout';
import TutorialsHero from './components/TutorialsHero';
import TutorialsList from './components/TutorialsList';

const TutorialsPage = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // 设置页面 title 和 description
    document.title = t('tutorials:meta.title');
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', t('tutorials:meta.description'));
    
    // 设置 canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = `${siteUrl}/tutorials`;

    // 添加 ItemList Schema
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "PDF Tools Tutorials",
      "description": "Step-by-step tutorials for using our free PDF conversion and editing tools",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "HowTo",
            "name": "How to Convert PDF to Word",
            "description": "Learn how to convert PDF documents to editable Word files while preserving formatting",
            "url": `${siteUrl}/pdf-to-word`,
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Upload PDF File",
                "text": "Click the upload button and select your PDF file from your device"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Convert to Word",
                "text": "Click the convert button and wait for the conversion to complete"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Download Word File",
                "text": "Download your converted Word document and edit as needed"
              }
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "HowTo",
            "name": "How to Convert PDF to Excel",
            "description": "Learn how to extract tables and data from PDF to Excel spreadsheets",
            "url": `${siteUrl}/pdf-to-excel`,
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Upload PDF File",
                "text": "Select and upload your PDF file containing tables or data"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Convert to Excel",
                "text": "Start the conversion process to extract data into Excel format"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Download Excel File",
                "text": "Download your Excel file with extracted tables and data"
              }
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "HowTo",
            "name": "How to Convert PDF to PowerPoint",
            "description": "Learn how to convert PDF documents to editable PowerPoint presentations",
            "url": `${siteUrl}/pdf-to-ppt`,
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Upload PDF File",
                "text": "Upload your PDF document that you want to convert to PowerPoint"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Convert to PPT",
                "text": "Click convert and wait for the PDF to be transformed into slides"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Download PowerPoint",
                "text": "Download your PowerPoint presentation and customize as needed"
              }
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "HowTo",
            "name": "How to Convert Images and PDFs",
            "description": "Learn how to convert between PDF and image formats (JPG, PNG)",
            "url": `${siteUrl}/image-convert`,
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Upload File",
                "text": "Upload your PDF or image file (JPG, PNG supported)"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Select Format",
                "text": "Choose your desired output format (PDF to Image or Image to PDF)"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Download Converted File",
                "text": "Download your converted file in the selected format"
              }
            ]
          }
        },
        {
          "@type": "ListItem",
          "position": 5,
          "item": {
            "@type": "HowTo",
            "name": "How to Use PDF Tools (Merge, Split, Compress)",
            "description": "Learn how to merge, split, and compress PDF files",
            "url": `${siteUrl}/pdf-tools`,
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Select Tool",
                "text": "Choose the tool you need: Merge, Split, or Compress PDF"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Upload PDF Files",
                "text": "Upload one or multiple PDF files depending on the tool selected"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Process and Download",
                "text": "Process your files and download the result"
              }
            ]
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'tutorials-schema';
    script.textContent = JSON.stringify(itemListSchema);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('tutorials-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [t]);

  return (
    <Layout>
      <TutorialsHero 
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />
      <TutorialsList activeCategory={activeCategory} />
    </Layout>
  );
};

export default TutorialsPage;